import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import type { TeamKurz, TicketFilterAnfrage, TicketKarte, Unternehmen } from "@kundeninfo/api-client";
import { Auswahl, Auswahlmenue, Knopf, Optionen, Werkzeuggruppe } from "@kundeninfo/ui/me-design";
import { personOption } from "./Avatar.js";
import { HIERARCHY_TEXT } from "./hierarchyTexts.js";
import type { AvatarPerson } from "./Avatar.js";
import { SONDER_ZUSTAENDIG } from "./boardSpeicher.js";
import type { BoardFilter } from "./boardSpeicher.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DIE FILTER DES BOARDS (Spec „Board“ 115, F35, F129) — Unternehmen, Kunde, Zuständig (me-auswahlmenue
 * mit Avatar je Option und im Auslöser, F48/Entscheid 53) und „nur verknüpfte“ als Umschalt-Chip
 * (`me-knopf gestalt="pille"` mit `gedrueckt`, 61d). Board und Liste teilen sie (Spec „Liste“, Ruling A4)
 * samt Gedächtnis (`boardSpeicher.ts`). Gibt die vier Gruppen (`me-werkzeuggruppe`, Name als
 * zugänglicher Name und Sofort-Popup, `aktiv`, wenn der Filter von der Vorgabe abweicht) für den Slot
 * `filter` der Werkzeugleiste zurück; die Leiste drumherum gehört der Ansicht. Kunden kommen aus den
 * gezeigten Tickets, Personen aus `listTicketAssignees` (Rückfall: die Zuständigen der Tickets).
 */

export function filterAnfrage(f: BoardFilter): TicketFilterAnfrage {
  return {
    ...(f.company !== "ALLE" ? { company: f.company } : {}),
    ...(f.customer !== "" ? { customer: f.customer } : {}),
    ...(f.assignee !== "" ? { assignee: f.assignee } : {}),
    ...(f.linked ? { linked: true } : {}),
    ...((f.team ?? "") !== "" ? { team: f.team } : {}),
  };
}

/** Wie viele der vier Filter von der Vorgabe abweichen — der Zähler an „Filter (n)“ und die Bedingung
 *  für „Zurücksetzen“ der Werkzeugleiste (F129, 61f, 61l). */
export function anzahlAktiverFilter(f: BoardFilter): number {
  return [f.company !== "ALLE", f.customer !== "", f.assignee !== "", f.linked, (f.team ?? "") !== ""]
    .filter((x) => x).length;
}

export type TicketFilterleisteProps = {
  client: Pick<TicketsClient, "listTicketAssignees" | "listMeineTeams">; companies: readonly Unternehmen[];
  /** Die eigene Person für „Ich“; ohne sie entfällt die Wahl. */
  ich: { id: string; name: string } | null;
  tickets: readonly TicketKarte[]; filter: BoardFilter; onFilter: (f: Partial<BoardFilter>) => void;
};

export function TicketFilterleiste(p: TicketFilterleisteProps): ReactElement {
  // Der Name des gewählten Kunden nur im Speicher der Sitzung (nie im Browserspeicher).
  const [kundeName, setKundeName] = useState("");
  const [personen, setPersonen] = useState<readonly AvatarPerson[]>([]);
  const ichId = p.ich?.id ?? null;

  // Wer zuständig sein kann (mit Mail für den Steckbrief, F48) — einmal beim Öffnen; ohne Antwort
  // bleiben die Zuständigen der Tickets.
  useEffect(() => {
    let aktiv = true;
    p.client.listTicketAssignees()
      .then((r) => { if (aktiv) setPersonen(r.items); })
      .catch(() => { /* Rückfall: die Zuständigen der Tickets */ });
    return () => { aktiv = false; };
  }, [p.client]);

  // Teamgruppe (F134): nur Teams, in denen man Mitglied ist; ADMIN ohne Mitgliedschaft bekommt keine Gruppe.
  // `null` = noch nicht geladen (dann wird auch kein gemerkter Teamfilter verworfen).
  const [teams, setTeams] = useState<readonly TeamKurz[] | null>(null);
  useEffect(() => {
    let aktiv = true;
    // 7-6: Scheitert der Abruf, gilt „keine Teams“ — ein gemerkter Teamfilter fällt dann auf „Alle“ zurück,
    // statt unsichtbar weiterzuwirken.
    p.client.listMeineTeams().then((r) => { if (aktiv) setTeams(r.items); })
      .catch(() => { if (aktiv) setTeams([]); });
    return () => { aktiv = false; };
  }, [p.client]);
  const eigeneTeams = useMemo(() => (teams ?? []).filter((t) => t.mitglied), [teams]);
  const filterTeam = p.filter.team ?? "";
  const onFilter = p.onFilter;
  useEffect(() => {
    // Review-Fokus 2: Ein gemerkter Teamfilter, der kein eigenes Team mehr nennt, fällt auf „Alle“ zurück.
    if (teams !== null && filterTeam !== "" && !eigeneTeams.some((t) => t.id === filterTeam)) onFilter({ team: "" });
  }, [teams, eigeneTeams, filterTeam, onFilter]);

  const kunden = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of p.tickets) for (const c of t.customers) m.set(c.id, c.name);
    if (p.filter.customer !== "" && !m.has(p.filter.customer)) {
      m.set(p.filter.customer, kundeName !== "" ? kundeName : "gewählter Kunde");
    }
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [p.tickets, p.filter.customer, kundeName]);
  const zustaendige = useMemo(() => {
    const m = new Map<string, AvatarPerson>();
    for (const t of p.tickets) if (t.assignee !== null) m.set(t.assignee.id, t.assignee);
    for (const u of personen) m.set(u.id, u);
    if (p.filter.assignee !== "" && !SONDER_ZUSTAENDIG.includes(p.filter.assignee) && !m.has(p.filter.assignee)) {
      m.set(p.filter.assignee, { id: p.filter.assignee, name: "gewählte Person" });
    }
    if (ichId !== null) m.delete(ichId);
    return [...m.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [p.tickets, personen, p.filter.assignee, ichId]);
  const ichPerson: AvatarPerson | null = useMemo(() => {
    if (p.ich === null) return null;
    return personen.find((u) => u.id === p.ich!.id) ?? { id: p.ich.id, name: p.ich.name };
  }, [personen, p.ich]);

  return (
    <>
      <Werkzeuggruppe beschriftung="Unternehmen" aktiv={p.filter.company !== "ALLE"}>
        <Optionen aria-label="Unternehmen" darstellung="segmente" richtung="waagerecht" wert={p.filter.company}
          onGeaendert={(d) => p.onFilter({ company: d.wert ?? "ALLE" })}>
          <option value="ALLE">Alle</option>
          {p.companies.map((c) => <option key={c.id} value={c.id}>{c.shortCode}</option>)}
        </Optionen>
      </Werkzeuggruppe>
      {eigeneTeams.length > 0 && (
        // Je eigenes Team ein Umschalt-Chip (61d-Muster); höchstens einer gedrückt, erneuter Klick hebt auf.
        <Werkzeuggruppe beschriftung="Team" aktiv={filterTeam !== ""}>
          {eigeneTeams.map((t) => (
            <Knopf key={t.id} art="sekundaer" gestalt="pille" gedrueckt={filterTeam === t.id}
              onClick={() => p.onFilter({ team: filterTeam === t.id ? "" : t.id })}>
              {t.shortCode}
            </Knopf>
          ))}
        </Werkzeuggruppe>
      )}
      <Werkzeuggruppe beschriftung="Kunde" aktiv={p.filter.customer !== ""}>
        {/* Feste Breite 10 em (me-design da6be0f, `breite`; ersetzt Übergang T-13, F130): Ein langer Kundenname
            drückt die Leiste nicht in eine tiefere Stufe; das Feld kürzt ihn mit „…“ und nennt ihn im
            Sofort-Popup voll. */}
        <Auswahl kompakt breite="10em" beschriftung="Kunde" wert={p.filter.customer}
          onGeaendert={(d) => {
            setKundeName(kunden.find(([id]) => id === d.wert)?.[1] ?? "");
            p.onFilter({ customer: d.wert });
          }}>
          <option value="">Alle Kunden</option>
          {kunden.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </Auswahl>
      </Werkzeuggruppe>
      {/* Die Werte bleiben die des Filtermodells ("" alle, Kennung, "none", "queue", "delegated"); die Sonderoptionen
          tragen data-art, damit das Element sie oben mit Gruppensymbol, eigenem Avatar bzw.
          gestricheltem Kreis zeigt und von der Suche ausnimmt. „Warteschlange meiner Teams“ und „Von mir delegiert“
          sind gewöhnliche Optionen direkt dahinter (me-auswahlmenue kennt nur alle|ich|niemand; deshalb durchsuchbar,
          Festlegung 10, Wunsch W4). */}
      {/* Feste Breite 10 em am Auslöser (me-design da6be0f, `breite`, F130), wie beim Kunden. */}
      <Werkzeuggruppe beschriftung="Zuständig" aktiv={p.filter.assignee !== ""}>
        <Auswahlmenue kompakt breite="10em" beschriftung="Zuständig" wert={p.filter.assignee}
          onGeaendert={(d) => p.onFilter({
            assignee: d.art === "alle" ? "" : d.art === "niemand" ? "none" : d.wert })}>
          <option value="" data-art="alle">Alle</option>
          {ichPerson !== null && <option value={ichPerson.id} data-art="ich" {...personOption(ichPerson)}>Ich</option>}
          <option value="none" data-art="niemand">Niemand</option>
          <option value="queue">{HIERARCHY_TEXT.filterQueue}</option>
          <option value="delegated">{HIERARCHY_TEXT.filterDelegated}</option>
          {zustaendige.map((u) => <option key={u.id} value={u.id} {...personOption(u)}>{u.name}</option>)}
        </Auswahlmenue>
      </Werkzeuggruppe>
      <Werkzeuggruppe beschriftung="Verknüpfung" aktiv={p.filter.linked}>
        <Knopf art="sekundaer" gestalt="pille" gedrueckt={p.filter.linked}
          onClick={() => p.onFilter({ linked: !p.filter.linked })}>
          nur verknüpfte
        </Knopf>
      </Werkzeuggruppe>
    </>
  );
}
